import React, { useState } from "react";
import { Form, Input, Button, Flex, Modal, message } from "antd";
import type { GetProps } from "antd";
import backarrow from "../../../../public/images/arrow_back.svg";
import successicon from "../../../../public/images/success-tick.svg";
import "./Forgot.scss";
import "../style.scss";
import {
  useGenerateOtp,
  useVerifyOtp,
  useResetPassword,
} from "../../../hooks/useOtp";
import { HeaderIcon } from "../../../layout/Components/icons";
import { useNavigate } from "react-router";

interface FormValues {
  identifier: string;
}

type OTPProps = GetProps<typeof Input.OTP>;

const ForgotPassword: React.FC = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isOtpScreen, setIsOtpScreen] = useState(false);
  const [otpValues, setOtpValues] = useState<string[]>(["", "", "", "", ""]);
  const [isResetScreen, setIsResetScreen] = useState(false);
  const [identifier, setIdentifier] = useState<string>("");
  const [otpVerificationId, setOtpVerificationId] = useState<number | null>(
    null
  );
  const [userId, setUserId] = useState<string | null>(null);
  const [passwordStrength, setPasswordStrength] = useState("");

  const [form] = Form.useForm<FormValues>();

  const { mutate: generateOtp, isPending: sendingOtp } = useGenerateOtp();
  const { mutate: verifyOtp, isPending: verifyingOtp } = useVerifyOtp();
  const { mutate: resetPassword, isPending: resettingPassword } =
    useResetPassword();
  const navigate = useNavigate();
  const onChange: OTPProps["onChange"] = (text) => {};
  const onInput: OTPProps["onInput"] = (value) => {};
  const sharedProps: OTPProps = { onChange, onInput };

  // Helper to mask displayed contact
  const formatIdentifierForDisplay = (value: string): string => {
    if (!value) return "";
    const phoneRegex = /^[\d\s\-\+\(\)]+$/;
    if (phoneRegex.test(value.replace(/\s/g, ""))) {
      if (value.length > 4) {
        const visiblePart = value.slice(-4);
        const maskedPart = "*".repeat(Math.max(0, value.length - 4));
        return maskedPart + visiblePart;
      }
      return value;
    }
    if (value.includes("@")) {
      const [localPart, domain] = value.split("@");
      if (localPart.length > 2) {
        const maskedLocal =
          localPart.charAt(0) +
          "*".repeat(Math.max(0, localPart.length - 2)) +
          localPart.slice(-1);
        return maskedLocal + "@" + domain;
      }
      return value;
    }
    if (value.length > 4) {
      return (
        value.slice(0, 2) +
        "*".repeat(Math.max(0, value.length - 4)) +
        value.slice(-2)
      );
    }
    return value;
  };
  function evaluatePasswordStrength(password: any) {
    let count = 0;

    if (/\d/.test(password)) count++; // number
    if (/[A-Z]/.test(password)) count++; // uppercase
    if (/[a-z]/.test(password)) count++; // lowercase
    if (password.length >= 8 && password.length <= 30) count++; // length

    if (count === 4) return { label: "Strong", level: 4 };
    if (count >= 2) return { label: "Fair", level: count };
    return { label: "Weak", level: count };
  }

  const strengthMap: Record<"Weak" | "Fair" | "Strong", number> = {
    Weak: 1,
    Fair: 2,
    Strong: 3,
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { label } = evaluatePasswordStrength(e.target.value);
    setPasswordStrength(label);
  };

  const handleSendOtp = () => {
    form
      .validateFields()
      .then(() => {
        const username = form.getFieldValue("identifier");
        generateOtp(
          {
            receiver_id: username,
            purpose: "reset password",
            country_code: "27",
          },
          {
            onSuccess: (res: any) => {
              setUserId(res?.data?.user_id);
              setOtpVerificationId(res?.data?.otp_verification_id);
              setIsOtpScreen(true);
              setOtpValues(["", "", "", "", ""]);
            },
            onError: (err: any) => {
              message.error(
                err?.response?.data?.message || "Failed to send OTP"
              );
              console.error("Generate OTP Error:", err);
            },
          }
        );
      })
      .catch(() => {
        message.error("Please enter your registered ID");
      });
  };
  const handleVerifyOtp = (values: any) => {
    const otpValue = values.otp;
    if (!otpValue || otpValue.length !== 5) {
      message.warning("Please enter the 5-digit OTP");
      return;
    }
    if (!otpVerificationId) {
      message.warning("OTP session expired. Please resend OTP.");
      return;
    }

    verifyOtp(
      { otp_verification_id: otpVerificationId, otp: Number(otpValue) },
      {
        onSuccess: () => {
          message.success("OTP verified successfully");
          setIsOtpScreen(false);
          setIsResetScreen(true);
        },
        onError: (err: any) => {
          message.error(
            err?.response?.data?.message || "OTP verification failed"
          );
          console.error("Verify OTP Error:", err);
        },
      }
    );
  };
  const handleResendOtp = () => {
    const username = form.getFieldValue("identifier");
    if (!username) return;
    generateOtp(
      { receiver_id: username, purpose: "sign up", country_code: "27" },
      {
        onSuccess: (res) => {
          setUserId(res.data?.user_id);

          setOtpVerificationId(res.data.otp_verification_id);
          setOtpValues(["", "", "", "", ""]);
          message.success("OTP resent successfully");
        },
        onError: (err: any) => message.error("Resend OTP failed:", err),
      }
    );
  };

  const handleResetPassword = (values: any) => {
    if (!userId) {
      message.error("Invalid session. Please try again.");
      return;
    }

    resetPassword(
      { user_id: userId, password: values.password },
      {
        onSuccess: () => {
          message.success("Password reset successful");
          setIsModalOpen(true);
        },
        onError: (err: any) => {
          const errorMessage =
            err?.response?.data?.message || "Failed to reset password";
          message.error(errorMessage);
        },
      }
    );
  };

  const handleOk = () => {
    setIsModalOpen(false),
      navigate("/login"),
      setOtpValues(["", "", "", "", ""]);
  };
  const handleCancel = () => setIsModalOpen(false);

  return (
    <>
      <div className="auth-layout-section">
        <div className="header-area">
          <HeaderIcon />
        </div>
        <div className="switchable-area">
          {/*  FORGOT SCREEN */}
          {!isOtpScreen && !isResetScreen && (
            <div className="login-provider default-box-area">
              <a href="login">
                <img src={backarrow} alt="back-arrow" />
              </a>
              <div className="page-heading">
                <h1 className="title heading-bold mb-2">Forgot password?</h1>
                <p className="subtitle description">
                  Please enter your registered mobile number to send a
                  verification code.
                </p>
              </div>
              <div className="auth-form-container">
                <Form form={form} className="login-form" layout="vertical">
                  <Form.Item<FormValues>
                    name="identifier"
                    className="custom-error"
                    rules={[
                      { required: true, message: "Please enter your ID" },
                    ]}
                  >
                    <Input
                      id="identifier"
                      placeholder="Email/Mobile number"
                      value={identifier}
                      className="forgot-input"
                      onChange={(e) => setIdentifier(e.target.value)}
                    />
                  </Form.Item>

                  <Button
                    type="primary"
                    className="login-form-button"
                    onClick={handleSendOtp}
                  >
                    {sendingOtp ? "Sending OTP..." : "Send OTP"}
                  </Button>
                </Form>
              </div>
            </div>
          )}

          {/* OTP VERIFICATION SCREEN */}
          {isOtpScreen && (
            <div className="otp-provider default-box-area">
              <div className="page-heading">
                <h1 className="title heading-bold mb-2">OTP Verification</h1>
                <p className="subtitle description">
                  A four digit PIN has been sent to{" "}
                  {identifier
                    ? formatIdentifierForDisplay(identifier)
                    : "your registered contact"}{" "}
                  <span
                    onClick={() => setIsOtpScreen(false)}
                    style={{
                      cursor: "pointer",
                      textDecoration: "underline",
                      color: "#1890ff",
                    }}
                  >
                    Change
                  </span>
                </p>
              </div>

              <div className="auth-form-container">
                <Form
                  form={form}
                  className="login-form"
                  onFinish={handleVerifyOtp}
                  layout="vertical"
                >
                  <div className="otp-area">
                    <Flex gap="middle" align="flex-start" vertical>
                      <Form.Item
                        name="otp"
                        rules={[{ required: true, message: "Enter OTP" }]}
                      >
                        <Input.OTP length={5} {...sharedProps} />
                      </Form.Item>
                    </Flex>
                  </div>
                  <div className="otp-helper-area">
                    <p>Haven't received an OTP yet?</p>
                    <span onClick={handleResendOtp}>Resend in </span>
                    <p>
                      Need help? <span>Contact support</span>
                    </p>
                  </div>

                  <Button
                    type="primary"
                    className="login-form-button"
                    htmlType="submit"
                    loading={verifyingOtp}
                  >
                    Next
                  </Button>
                </Form>
              </div>
            </div>
          )}

          {/*  RESET PASSWORD SCREEN  */}
          {isResetScreen && (
            <div className="password-provider default-box-area">
              <div className="page-heading">
                <h1 className="title heading-bold mb-2">Reset Password</h1>
                <p className="subtitle description">
                  Your new password should be different from the previous
                  password
                </p>
              </div>
              <div className="auth-form-container">
                <Form
                  form={form}
                  className="login-form"
                  onFinish={handleResetPassword}
                  layout="vertical"
                >
                  <Form.Item
                    name="password"
                    rules={[
                      { required: true, message: "Enter new password" },
                      {
                        pattern: /^(?=.*[A-Z])(?=.*[a-z])(?=.*\d)[^\s]{8,30}$/,
                        message:
                          "Password must be 8–30 characters long, contain uppercase, lowercase, and a digit, and have no spaces.",
                      },
                    ]}
                  >
                    <Input.Password
                      maxLength={30}
                      placeholder="New password"
                      onChange={handlePasswordChange}
                    />
                  </Form.Item>

                  <Form.Item
                    name="confirmPassword"
                    dependencies={["password"]}
                    validateTrigger={["onChange", "onBlur"]}
                    rules={[
                      ({ getFieldValue }) => ({
                        validator(_, value) {
                          const password = getFieldValue("password");

                          if (!value) {
                            return Promise.reject(
                              new Error("Confirm your password")
                            );
                          }

                          if (value !== password) {
                            return Promise.reject(
                              new Error("Passwords do not match!")
                            );
                          }

                          return Promise.resolve();
                        },
                      }),
                    ]}
                  >
                    <Input.Password placeholder="Confirm password" />
                  </Form.Item>

                  {passwordStrength && (
                    <div className="strength-visualizer">
                      <div className="strength-bars">
                        {[0, 1, 2].map((i) => (
                          <div
                            key={i}
                            className={`bar ${
                              i <
                              strengthMap[
                                passwordStrength as "Weak" | "Fair" | "Strong"
                              ]
                                ? passwordStrength.toLowerCase()
                                : ""
                            }`}
                          />
                        ))}
                      </div>
                      <span className="strength-label">{passwordStrength}</span>
                    </div>
                  )}

                  <div className="password-helper-area">
                    <p className="mb-0">Your password must:</p>
                    <ul>
                      <li>
                        Contain at least one number, upper and lower case letter
                      </li>
                      <li>Be between 8 and 30 characters in length</li>
                      <li>Try to keep your user ID out of the password</li>
                    </ul>
                  </div>

                  <Button
                    type="primary"
                    className="login-form-button"
                    htmlType="submit"
                    loading={resettingPassword}
                  >
                    Next
                  </Button>

                  <Modal
                    title=""
                    open={isModalOpen}
                    onOk={handleOk}
                    onCancel={handleOk}
                    className="confirm-modal"
                    footer={null}
                  >
                    <div className="success-pic">
                      <img src={successicon} alt="success" />
                    </div>
                    <h1>Password reset successful</h1>
                    <p>
                      Your password has been successfully reset. You can now log
                      in with your new password.
                    </p>
                    <Button
                      type="primary"
                      className="login-form-button"
                      onClick={handleOk}
                    >
                      OK
                    </Button>
                  </Modal>
                </Form>
              </div>
            </div>
          )}
        </div>

        <div className="footer-area">
          <p>
            By logging in Field force management, you agree to our{" "}
            <span>privacy policy</span> and <span>terms of service.</span>
          </p>
          <p>
            ©field force management <span>contact</span>
          </p>
        </div>
      </div>
    </>
  );
};

export default ForgotPassword;
